/**
 * Turn stored fills into chart markers.
 *
 * Entries AND exits, drawn as different things. The entry is the decision —
 * where a trade was put on, which way it bet, who put it on. The exit is what
 * became of it. The chart says so with geometry rather than with a legend: an
 * entry arrow sits LEFT of its bar pointing right, into time; an exit sits
 * RIGHT of its bar pointing left, back the way it came; and a faint dotted line
 * runs from one tip to the other, so the two arrows point at each other along
 * the trade they bracket.
 *
 * Entries carry who took them — bot or manual — because that is the thing
 * worth knowing later: a losing entry you made is a different lesson from one
 * the bot made. Exits carry NO attribution, deliberately: a position opened by
 * the bot can be closed by hand, so naming a bot on the exit would assert
 * something the fill does not know.
 *
 * Neither kind can be read off a fill. Nothing on the row says which it was,
 * and side alone calls every sell an exit — wrong the moment you go short. It
 * has to be walked out of the net position, and that walk is anchored to the
 * broker's CURRENT net position and runs BACKWARD, because the stored fill
 * history is lossy and does not begin at flat. See {@link buildTradeMarkers}
 * for why walking forward from zero mislabels trades.
 *
 * WHICH entry an exit closed is a second question, answered separately and
 * FIFO — see {@link matchClosures}. It is deliberately not folded into the
 * classification walk: that walk was hard-won and only ever has to answer
 * "opened or closed", which is a property of one fill against the running net.
 * Lot matching needs the whole queue of open lots, which is a different piece
 * of state entirely.
 *
 * WHAT the round trip made is the third question, and it is answered here
 * rather than in the popup that displays it — see {@link RealizedPnL}. The
 * popup is handed a marker and nothing else; the lot detail that P&L needs (how
 * much of which entry, at what price) exists only inside the matcher, and
 * re-deriving it downstream would mean a second FIFO walk that could disagree
 * with the lines already drawn on the chart.
 *
 * WHAT IT COST TO GET THERE is the fourth, and the only one that needs
 * something no fill carries: the bars the trade lived through. See
 * {@link AdverseExcursion}. A realised +6 points says nothing about whether the
 * trade sat 20 points underwater first, and that is the whole difference
 * between a setup worth repeating and one that got away with it. The bars come
 * in as an OPTIONAL argument, structurally typed ({@link Bar}), so this module
 * still imports no store and still runs in a test with no chart behind it —
 * and every caller that predates MAE keeps working, measuring nothing rather
 * than measuring wrongly.
 */

import { getInstrumentSpec } from '../config/instruments';

export type TradeSide = 'buy' | 'sell';

/** One stored fill, as `/api/trade-transactions` returns it. */
export interface FillRow {
  id: string;
  fillId?: string | null;
  timestamp: number;
  symbol: string;
  side: string;
  quantity: number;
  price: number;
  status: string;
  source?: string | null;
  botId?: string | null;
  botName?: string | null;
}

/** Opened or added to a position, versus reduced or closed one. */
export type TradeMarkerKind = 'entry' | 'exit';

/**
 * One chart bar, reduced to the two prices an excursion is measured against.
 *
 * Structurally typed on purpose, the same way {@link BrokerPositionSnapshot}
 * is: the store's `ExtendedCandle[]` satisfies this as-is, so the caller passes
 * what it already holds and this module still imports nothing from a store.
 *
 * `timestamp` is the bar's OPEN time in MILLISECONDS — the unit fills are in,
 * and the unit `marketStore` keeps candles in. {@link snapToBar} works in
 * seconds instead because that is what lightweight-charts hands the primitive;
 * nothing on this path goes near the chart.
 */
export interface Bar {
  timestamp: number;
  high: number;
  low: number;
}

/** Shared, so "no bars were supplied" does not allocate on every call. */
const NO_BARS: readonly Bar[] = Object.freeze([]);

/**
 * The worst a closed trade went AGAINST you before it closed — max adverse
 * excursion. The number that separates a comfortable winner from one that came
 * within a tick of stopping you out.
 *
 * ```
 * long :  entryPrice − lowest low   over [entry bar, exit bar]
 * short:  highest high − entryPrice over [entry bar, exit bar]
 * ```
 *
 * ## A magnitude, never signed
 *
 * `points` is >= 0 by construction — it is how far the wrong way, and the
 * direction is already fixed by the word "adverse". `dollars` is likewise a
 * positive magnitude; the popup is what writes the minus sign, because that is
 * a presentation decision and not a fact about the trade. The `Math.max(0, …)`
 * that guarantees it is not decorative: a fill priced outside its own bar's
 * range (slippage past the bar's low, or a bar the engine revised later) would
 * otherwise come out negative and read as a FAVOURABLE excursion, which is the
 * opposite of what this measures.
 *
 * A genuine `0` means the trade never once traded through its entry the wrong
 * way. That is a strong and useful claim, and it is why "we could not measure
 * it" is a `null` {@link RealizedPnL.mae} rather than a zero here — the same
 * distinction {@link RealizedPnL} draws between an unknown P&L and a scratch.
 *
 * ## Bar resolution, not tick resolution
 *
 * Bars are the finest thing available on this path; the engine's tick stream is
 * not retained far enough back to walk a trade from last week. So this is a
 * LOWER BOUND: the true intrabar worst is at least this deep and can be deeper,
 * because a bar low only says the market got there, not the path it took
 * inside. Everything that displays this has to say so — the popup prefixes the
 * figure with a tilde and explains it on hover.
 *
 * ## Blended exits: measured per lot, and the WORST is taken
 *
 * An exit that swallowed three scale-in lots is three trades sharing one fill.
 * Each lot has its own entry price and its own window — a lot added ten minutes
 * later simply did not live through the drawdown the first one sat through.
 * Measuring from the earliest entry across the whole closed quantity would
 * charge the later lots with heat they never took, which is exactly the
 * flattering-then-alarming error you get from averaging: scale in near the
 * bottom and the pre-add drawdown gets billed to contracts that were never
 * exposed to it.
 *
 * So each lot is measured over its own life and the deepest one is reported.
 * `points` is that lot's excursion PER CONTRACT — the figure a stop is sized in
 * — and `dollars` and `qty` are scoped to that same lot, so the three numbers
 * describe one thing rather than being blended across scopes. Note that makes
 * `dollars` a different scope from {@link RealizedPnL.dollars}, which spans the
 * whole close; the popup says which quantity it covers rather than leaving the
 * reader to assume.
 */
export interface AdverseExcursion {
  /** Deepest adverse move, in points per contract. Never negative. */
  points: number;
  /** That same excursion in dollars, on `qty` contracts. Never negative. */
  dollars: number;
  /** Contracts the worst lot contributed to this close — the scope of `dollars`. */
  qty: number;
}

/**
 * What a closing fill actually made, on the quantity it actually closed.
 *
 * Attached to any fill that consumed an open lot — every exit, and the closing
 * half of a reversal. Absent (`null`) on a fill that closed nothing, which is
 * the ordinary entry.
 *
 * ## Points is the primitive; dollars needs the contract
 *
 * Points comes out of the prices alone. Dollars needs the point value from the
 * instrument spec ({@link getInstrumentSpec}), which is why {@link FillRow}'s
 * own symbol is what prices it — MNQ is $2/point and MES is $5/point, and a
 * hard-coded multiplier would be silently wrong on every instrument but one.
 *
 * ## Per-contract, matching the rest of the codebase
 *
 * `points` is signed points PER CONTRACT and `dollars` is the total, which is
 * the convention `calculatePnL` in the instrument config and `RoundTrip` in the
 * Rust bot both use (`dollars = points x pointValue x qty`). An exit that
 * swallowed several lots bought at different prices has no single entry price,
 * so `points` there is the qty-weighted mean — but it is NOT computed by
 * averaging the entry prices. Each lot is priced against the exit on its own
 * and the results are summed, then divided by the closed quantity. The two
 * agree arithmetically while the multiplier is constant; summing per lot is
 * what stays right if it ever isn't, and it is the same thing the broker does.
 *
 * ## Absent is not zero
 *
 * A close of a position the stored history never saw opened has no entry price
 * to measure against — see {@link matchClosures}. That case is `points` and
 * `dollars` of `null` with `unknownQty` accounting for the contracts, never a
 * zero, because a scratch trade is a real and different outcome. A partly
 * unknown close (some lots known, some not) reports P&L on the known part only
 * and leaves the rest in `unknownQty`, so the number is never quietly claiming
 * to cover more than it does.
 */
export interface RealizedPnL {
  /** Contracts closed against a KNOWN entry lot — what the P&L is measured on. */
  qty: number;
  /**
   * Contracts closed against the pre-history phantom lot. Their entry price is
   * not merely unrecorded, it is unknowable from the stored fills.
   */
  unknownQty: number;
  /** Signed points per contract, qty-weighted. `null` when `qty` is 0. */
  points: number | null;
  /** Signed dollars, total across the closed lots. `null` when `qty` is 0. */
  dollars: number | null;
  /** Qty-weighted mean entry price over the known lots. `null` when `qty` is 0. */
  entryPrice: number | null;
  /**
   * Milliseconds from the OLDEST lot this fill closed to the fill itself —
   * how long the position being unwound had been on. `null` when `qty` is 0.
   */
  heldMs: number | null;
  /**
   * The worst this went against you before closing — see
   * {@link AdverseExcursion}. `null` means UNMEASURABLE, never "flat":
   *
   *  - no bars were supplied at all (the caller has no chart loaded);
   *  - a lot's entry predates the oldest loaded bar, so the window cannot be
   *    walked;
   *  - every lot was the pre-history phantom, which has no entry price to
   *    measure from — the same reason `points` is null there.
   *
   * All-or-nothing across the KNOWN lots: if one of them cannot be measured
   * this is `null` rather than the worst of the rest, because "the worst" is a
   * claim about all of them and a partial maximum could understate without
   * saying so. The phantom lots are the exception — they are excluded here just
   * as they are from `points`, and `unknownQty` is what already declares them.
   */
  mae: AdverseExcursion | null;
}

/** One fill, ready to draw. */
export interface TradeMarker {
  /**
   * Stable per-fill key, so a click can be resolved back to the marker it hit
   * without holding the object itself in React state. Same key the dedupe uses:
   * the broker's fill id when there is one, otherwise the natural key.
   *
   * Also what {@link TradeMarker.closes} refers to, which is the other reason
   * it has to be stable: a connector line is stored as a pair of these.
   */
  id: string;
  /**
   * Fill time in ms — wall clock, NOT a bar time. It will usually fall in the
   * middle of a bar rather than on its opening edge. See {@link snapToBar}.
   */
  timestamp: number;
  price: number;
  /**
   * The side of the FILL, not of the position.
   *
   * On an entry the two are the same thing — buying opens or adds to a long —
   * so the arrow points the way the position now bets. On an exit they are
   * opposite by definition: a long is closed with a sell. Colouring by the
   * fill side either way keeps one rule for the hue (teal bought, red sold)
   * and makes a round trip read as a teal arrow answered by a red one.
   */
  side: TradeSide;
  qty: number;
  kind: TradeMarkerKind;
  /**
   * Who opened it. Only ever set on entries — see the module note. `null` on an
   * entry means nothing claimed it, i.e. you took it by hand.
   */
  botName: string | null;
  /**
   * The signal this bot entry was taken on, when it can be resolved.
   *
   * Only ever set on BOT entries. A hand-taken fill has no signal behind it,
   * and an exit is not attributed at all — the bot that opened a position is
   * not evidence about who closed it, which is the same rule `botName` follows.
   *
   * Drawn as a connector from the signal to the entry, in the same dotted style
   * as the entry-to-exit line, so one trade reads as: signal fired here, filled
   * there, closed there.
   *
   * Resolved rather than stored: broker fills come back with no idea which
   * signal produced them, so this is matched from the bot's own execution
   * records — see `signalLinkResolver`.
   */
  signal?: { timestamp: number; price: number };
  /**
   * Ids of the entry markers whose lots this fill consumed, oldest lot first.
   * Empty on anything that opened without closing, and on a close of a position
   * the stored history never saw opened.
   *
   * A list rather than a single id because the relation genuinely is many to
   * many — see {@link pairClosures}. One connector line is drawn per element.
   *
   * Present on ENTRIES too, not just exits: a reversal closes the old position
   * and opens the new one in a single fill, and that fill is classified an
   * entry. Its `closes` is what keeps the lots it flattened from ending in a
   * dangling line.
   */
  closes: readonly string[];
  /**
   * What this fill realised, or `null` if it closed nothing.
   *
   * Set whenever a lot was consumed — so on every exit, and on the closing half
   * of a reversal, for the same reason {@link TradeMarker.closes} is. Note it is
   * NOT tied to `closes` being non-empty: a close of the pre-history position
   * names no entry yet still gets a `realized`, one that reports the quantity
   * as unattributable rather than reporting nothing at all. See
   * {@link RealizedPnL}.
   */
  realized: RealizedPnL | null;
}

/** Shared, so a marker that closed nothing does not allocate an array. */
const NO_CLOSES: readonly string[] = Object.freeze([]);

const isFilled = (f: FillRow) => (f.status ?? '').toLowerCase() === 'filled';

const sideOf = (raw: string): TradeSide | null => {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'buy' || s === 'b' || s === 'bought') return 'buy';
  if (s === 'sell' || s === 's' || s === 'sold') return 'sell';
  return null;
};

/** The dedupe key, also used as the marker id — see {@link TradeMarker.id}. */
const keyOf = (f: FillRow): string =>
  f.fillId || f.id || `${f.timestamp}:${f.side}:${f.quantity}:${f.price}`;

/** A fill that survived validation, with its side resolved and signed size. */
interface WalkableFill {
  fill: FillRow;
  side: TradeSide;
  /** Buys positive, sells negative — the change this fill makes to net. */
  signed: number;
}

/** One fill, reduced to what the lot matcher needs of it. */
export interface LotEvent {
  /** The fill's {@link TradeMarker.id} — what a link is expressed in. */
  id: string;
  /** Buys positive, sells negative. Never zero; zero-size fills are dropped. */
  signed: number;
  /**
   * Fill price. Carried through the queue so a close can be priced against the
   * particular lots it took rather than against the position's average, which
   * is the difference between a real FIFO P&L and an approximation of one.
   */
  price: number;
  /** Fill time in ms, so a close can say how long the lot had been open. */
  timestamp: number;
}

/** An open lot: what a fill opened, and how much of it has not been closed. */
interface OpenLot {
  /**
   * Marker id of the fill that opened it, or `null` for the position the stored
   * history begins already holding — see {@link matchClosures}.
   */
  entryId: string | null;
  remaining: number;
  /** Price the lot was opened at. `null` on the phantom lot, which has none. */
  price: number | null;
  /** When it was opened. `null` on the phantom lot, for the same reason. */
  timestamp: number | null;
}

/** One entry lot a closing fill consumed, and how much of it it took. */
export interface ClosedLot {
  /**
   * The entry's marker id, or `null` for the pre-history phantom lot — which
   * has no marker on the chart and no knowable entry price.
   */
  entryId: string | null;
  /** Contracts taken from this lot by this close. Not the lot's full size. */
  qty: number;
  /** `null` exactly when `entryId` is. */
  entryPrice: number | null;
  /** `null` exactly when `entryId` is. */
  entryTimestamp: number | null;
}

/**
 * Which entry LOTS each fill closed, and how much of each. FIFO, oldest first.
 *
 * This is what a connector line is drawn from, and what a realised P&L is
 * computed from. The classification walk in {@link buildTradeMarkers} answers
 * "did this fill open or close", which is all the arrow direction needs;
 * joining an exit to the entry it belongs to is a different question and needs
 * the whole queue of open lots to answer.
 *
 * Lots rather than bare ids because P&L is a per-lot quantity: an exit that
 * swallowed three scale-in lots made a different amount on each, and the ids
 * alone cannot say how much of which. {@link pairClosures} is the id-only view
 * of this same walk, derived rather than computed separately so the lines and
 * the numbers can never be matched differently.
 *
 * ## Why FIFO
 *
 * Because that is how the position itself is accounted for. A futures broker
 * closes the oldest lot first, and the realised P&L it reports is computed that
 * way, so matching any other order would draw lines that disagree with the
 * numbers on the account. FIFO also happens to give the reading you want on a
 * chart: scale in three times and scale out three times, and the lines nest
 * rather than cross.
 *
 * ## The relation is many to many, and the lines say so
 *
 * - **Partial close.** Buy 3, sell 1. The sell consumes one unit of the buy's
 *   lot and returns `[buy]`; two units stay open. One line, entry to exit.
 * - **Scale-out.** Buy 3, then sell 1 three times. Each sell returns `[buy]`,
 *   so the one entry ends up with three lines fanning out to three exits —
 *   which is exactly what happened.
 * - **Scale-in closed at once.** Buy 1 three times, then sell 3. That one sell
 *   consumes all three lots and returns all three ids, so three lines converge
 *   on the single exit. Collapsing that to one line would silently claim two of
 *   the entries were never closed.
 * - **Reversal.** Long 1, then sell 2. The sell closes the long AND opens a
 *   short; it is classified an ENTRY (the exposure it leaves you with is what
 *   the arrow has to say), and it still returns `[buy]` here, so the line from
 *   the long lands on it. One fill, two roles, no dangling entry.
 *
 * ## The position the history begins holding
 *
 * `openingNet` is `before(first fill)` — nonzero whenever the stored fills
 * start mid-position, which is the normal case (see {@link buildTradeMarkers}).
 * It is seeded as a lot with no `entryId`, so the fills that close it are still
 * matched — the queue stays in step — but produce no link, because there is no
 * entry marker on the chart to draw one to. Dropping the phantom lot instead
 * would push those closes onto the NEXT entry and draw a line to a trade that
 * had not been taken yet.
 *
 * The phantom lot IS returned here, as a `ClosedLot` with a null `entryId`,
 * where {@link pairClosures} drops it. That is the difference that matters to
 * P&L: a line cannot be drawn to it, but the quantity it accounts for still has
 * to be reported, or an exit that closed 3 contracts of which 2 predate the
 * history would silently show the P&L of 1 as if it were the whole trade.
 *
 * Pure: no store, no clock, no chart. `events` must be in time order.
 *
 * @returns fill id -> the lots it consumed, oldest first. Fills that consumed
 *   nothing are absent from the map rather than mapped to an empty array.
 */
export function matchClosures(
  events: readonly LotEvent[],
  openingNet: number
): Map<string, readonly ClosedLot[]> {
  const lots: OpenLot[] = [];
  if (openingNet !== 0) {
    lots.push({ entryId: null, remaining: Math.abs(openingNet), price: null, timestamp: null });
  }

  const out = new Map<string, readonly ClosedLot[]>();
  let net = openingNet;

  for (const ev of events) {
    const size = Math.abs(ev.signed);
    // Split the fill into the part that ran AGAINST the position it found and
    // the part that opened fresh exposure. Both are nonzero only on a flip;
    // one of them is the whole fill in every other case.
    const closeQty =
      net !== 0 && Math.sign(ev.signed) !== Math.sign(net) ? Math.min(size, Math.abs(net)) : 0;
    const openQty = size - closeQty;

    if (closeQty > 0) {
      let left = closeQty;
      const closed: ClosedLot[] = [];
      // `lots.length > 0` is belt and braces: the queue's total remaining is
      // maintained equal to |net|, so it cannot run dry before `left` does. If
      // an anchor correction ever broke that invariant, stopping early draws
      // fewer lines — losing the walk is not worth a line.
      while (left > 0 && lots.length > 0) {
        const lot = lots[0];
        const taken = Math.min(left, lot.remaining);
        lot.remaining -= taken;
        left -= taken;
        // Each lot appears at most once per close: it is either drained (and
        // shifted off) or partially consumed, and a partial consumption always
        // exhausts `left`. The phantom lot is recorded like any other — with a
        // null id and no price — so its quantity is not lost.
        closed.push({
          entryId: lot.entryId,
          qty: taken,
          entryPrice: lot.price,
          entryTimestamp: lot.timestamp,
        });
        if (lot.remaining <= 0) lots.shift();
      }
      if (closed.length > 0) out.set(ev.id, closed);
    }

    if (openQty > 0) {
      lots.push({
        entryId: ev.id,
        remaining: openQty,
        price: ev.price,
        timestamp: ev.timestamp,
      });
    }
    net += ev.signed;
  }

  return out;
}

/**
 * The entry ids among a close's lots, in the order the lots were consumed.
 *
 * The phantom pre-history lot drops out here rather than in the walk: it has no
 * marker on the chart, so a connector to it would have one end.
 */
function entryIdsOf(lots: readonly ClosedLot[]): string[] {
  const ids: string[] = [];
  for (const lot of lots) {
    if (lot.entryId !== null) ids.push(lot.entryId);
  }
  return ids;
}

/**
 * Which entries each fill closed. FIFO, oldest first.
 *
 * The id-only view of {@link matchClosures}, kept as its own entry point
 * because "which entries did this close" is the whole question a connector line
 * asks and the answer reads better without the lot arithmetic attached. Derived
 * from the same single walk rather than repeating it — two FIFO passes could
 * drift apart, and lines that disagree with the P&L beside them are worse than
 * either alone.
 *
 * @returns fill id -> the entry ids it closed. Fills that closed nothing, and
 *   fills that closed only the pre-history phantom lot, are absent from the map
 *   rather than mapped to an empty array.
 */
export function pairClosures(
  events: readonly LotEvent[],
  openingNet: number
): Map<string, readonly string[]> {
  const out = new Map<string, readonly string[]>();
  for (const [fillId, lots] of matchClosures(events, openingNet)) {
    const ids = entryIdsOf(lots);
    if (ids.length > 0) out.set(fillId, ids);
  }
  return out;
}

/** Kill the float dust that subtracting two five-figure prices leaves behind. */
const roundTo = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

/**
 * Index of the bar containing `timeMs` — the last bar opening at or before it.
 *
 * `-1` when `timeMs` predates the first loaded bar. That is "we cannot see back
 * that far", and it has to stay distinguishable from bar 0: clamping to the
 * oldest bar instead would measure a trade from a window it never traded in and
 * report the answer as if it were the trade's own.
 *
 * A time NEWER than the last bar pins to the last bar, deliberately — a fill in
 * the bar still forming belongs to that bar, which the chart may not have drawn
 * yet. Same rule, and the same reasoning, as {@link snapToBar}.
 *
 * Binary search: a blended exit runs this twice per lot, on every rebuild, over
 * a candle array that routinely holds thousands of bars.
 */
function barIndexAt(timeMs: number, bars: readonly Bar[]): number {
  if (bars.length === 0) return -1;
  if (timeMs < bars[0].timestamp) return -1;

  let lo = 0;
  let hi = bars.length - 1;
  while (lo < hi) {
    // Round the midpoint UP so `lo` always advances and the loop terminates.
    const mid = lo + Math.ceil((hi - lo) / 2);
    if (bars[mid].timestamp <= timeMs) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * How far ONE lot went against itself, from its entry bar to its exit bar
 * inclusive, in points per contract. See {@link AdverseExcursion}.
 *
 * Inclusive at both ends, and that matters at both ends. The entry bar is where
 * an immediate spike against you happens — excluding it would silently drop the
 * most common adverse move there is. The exit bar is where a trade that was
 * stopped out took its worst tick, so excluding that one would flatter every
 * loser on the chart. A trade opened and closed inside a single bar is the
 * degenerate case of the same rule: one bar, scanned once.
 *
 * @returns the excursion, or `null` when the bars cannot answer — no bars, or
 *   an entry older than the oldest one loaded. Never a zero for those: zero is
 *   the real and different answer "it never went against you".
 */
function lotExcursion(
  entryPrice: number,
  entryTimestamp: number,
  exitTimestamp: number,
  wasLong: boolean,
  bars: readonly Bar[]
): number | null {
  const from = barIndexAt(entryTimestamp, bars);
  if (from < 0) return null;
  // A lot cannot exit before it opened, so this normally lands at or after
  // `from`. The clamp is belt and braces against a clock-skewed fill pair,
  // which would otherwise make the loop below scan nothing and report 0 — the
  // one answer that must never be produced by accident.
  const to = Math.max(from, barIndexAt(exitTimestamp, bars));

  let extreme = wasLong ? Infinity : -Infinity;
  for (let i = from; i <= to; i++) {
    const bar = bars[i];
    if (wasLong) {
      if (bar.low < extreme) extreme = bar.low;
    } else if (bar.high > extreme) {
      extreme = bar.high;
    }
  }

  // Adverse is a magnitude — see {@link AdverseExcursion}. A fill priced
  // outside its own bar's range would otherwise come out negative here and read
  // as a favourable excursion.
  return Math.max(0, wasLong ? entryPrice - extreme : extreme - entryPrice);
}

/**
 * What a closing fill made on the lots it consumed. See {@link RealizedPnL}.
 *
 * ## The direction comes from the ENTRY
 *
 * Not from the fill's own side. A sell that closes something is unwinding a
 * LONG — that is what made it a close — so it profits when the exit is above
 * the entry; a buy that closes is covering a short and profits when the exit is
 * below. Reading the direction off the closing fill instead would invert the
 * sign of every trade on the chart, which is the one error here that looks
 * plausible on screen: winners and losers simply swap.
 *
 * ## The heat, on the same walk
 *
 * The max adverse excursion is computed here rather than anywhere downstream
 * for the reason the P&L is: it is a per-lot quantity, and the lots exist only
 * inside the matcher. It needs one thing the fills do not carry — the bars —
 * so `bars` is an optional argument, and omitting it yields a `null`
 * {@link RealizedPnL.mae} rather than a zero. See {@link AdverseExcursion}.
 *
 * @param lots The lots this fill consumed, from {@link matchClosures}.
 * @param exit The closing fill: its price, its time, its own side (buy or
 *   sell), and the symbol whose contract spec prices the points in dollars.
 * @param bars Ascending bars covering the trade, for the excursion. Optional:
 *   with none, everything else is still computed and `mae` is `null`.
 */
export function realizedFromLots(
  lots: readonly ClosedLot[],
  exit: { price: number; timestamp: number; side: TradeSide; symbol: string },
  bars: readonly Bar[] = NO_BARS
): RealizedPnL | null {
  if (lots.length === 0) return null;

  // A sell closes a long, a buy closes a short. See the note above. The same
  // fact fixes which side of the bar the excursion is read off: a long is hurt
  // by lows, a short by highs.
  const wasLong = exit.side === 'sell';
  const pointValue = getInstrumentSpec(exit.symbol).pointValue;

  let qty = 0;
  let unknownQty = 0;
  let pointContracts = 0;
  let dollars = 0;
  let entryNotional = 0;
  let openedAt: number | null = null;
  // Worst per-lot excursion so far, and the lot that took it. Seeded below zero
  // rather than at zero so a genuine 0.0 is still recorded WITH its quantity —
  // `worstPoints < 0` is what "no lot was measured" means, and a zero seed
  // would make an untouched trade indistinguishable from an unmeasured one.
  let worstPoints = -1;
  let worstQty = 0;
  // One known lot we could not measure poisons the whole figure — see
  // {@link RealizedPnL.mae}.
  let maeUnmeasurable = false;

  for (const lot of lots) {
    if (lot.entryId === null || lot.entryPrice === null) {
      // The pre-history lot. Counted, never priced — see {@link RealizedPnL}.
      // Excluded from the excursion for exactly the same reason: with no entry
      // price there is nothing to measure the bars against.
      unknownQty += lot.qty;
      continue;
    }
    const points = wasLong ? exit.price - lot.entryPrice : lot.entryPrice - exit.price;
    qty += lot.qty;
    pointContracts += points * lot.qty;
    // Summed per lot rather than taken once off an average entry price. The two
    // agree while every lot shares one multiplier; this one keeps agreeing with
    // the broker's own FIFO accounting if that ever stops being true.
    dollars += points * lot.qty * pointValue;
    entryNotional += lot.entryPrice * lot.qty;
    if (lot.entryTimestamp !== null && (openedAt === null || lot.entryTimestamp < openedAt)) {
      openedAt = lot.entryTimestamp;
    }

    if (maeUnmeasurable) continue;
    if (lot.entryTimestamp === null) {
      // Priced but not timestamped. Should not happen — the two are null
      // together on the phantom lot and set together everywhere else — but
      // guessing a window is how you invent an excursion.
      maeUnmeasurable = true;
      continue;
    }
    const excursion = lotExcursion(
      lot.entryPrice,
      lot.entryTimestamp,
      exit.timestamp,
      wasLong,
      bars
    );
    if (excursion === null) maeUnmeasurable = true;
    else if (excursion > worstPoints) {
      worstPoints = excursion;
      worstQty = lot.qty;
    }
  }

  // `worstPoints < 0` covers the all-phantom close, which never entered the
  // branch above at all.
  const mae: AdverseExcursion | null =
    maeUnmeasurable || worstPoints < 0
      ? null
      : {
          points: roundTo(worstPoints, 4),
          dollars: roundTo(worstPoints * worstQty * pointValue, 2),
          qty: worstQty,
        };

  if (qty === 0) {
    // Every lot was the phantom. There is a real quantity here and no honest
    // number to put against it, so the number is absent rather than zero.
    return {
      qty: 0,
      unknownQty,
      points: null,
      dollars: null,
      entryPrice: null,
      heldMs: null,
      mae: null,
    };
  }

  return {
    qty,
    unknownQty,
    points: roundTo(pointContracts / qty, 4),
    dollars: roundTo(dollars, 2),
    entryPrice: roundTo(entryNotional / qty, 4),
    heldMs: openedAt === null ? null : Math.max(0, exit.timestamp - openedAt),
    mae,
  };
}

/**
 * The parts of a broker {@link resolveNetPositionAnchor} reads. Structural, so
 * `BrokerState[]` satisfies it without this module importing the store.
 */
export interface BrokerPositionSnapshot {
  status: string;
  positions: ReadonlyArray<{ symbol?: string; netPos: number }>;
  /** See `BrokerState.positionsLoadedAt` — `undefined` means never reported. */
  positionsLoadedAt?: number;
}

/**
 * The anchor for {@link buildTradeMarkers}: net position across brokers for the
 * charted instrument, or `null` when it cannot be known.
 *
 * ## Why an empty positions array is not enough
 *
 * The first cut of this read "no rows means flat". That is wrong in the one
 * direction that matters. A broker is constructed with `positions: []` and
 * `broker:positions` replaces the array wholesale, so empty means EITHER flat
 * OR nothing has arrived yet. Treating empty as unknown looks conservative
 * until you notice that flat is the normal resting state of an account —
 * measured on staging, both brokers report `positions: []` with the user flat,
 * and the markers vanished from the chart entirely. Being unable to name the
 * common case is worse than the mislabelling this anchor was added to fix.
 *
 * `positionsLoadedAt` is the missing bit: it distinguishes a snapshot that said
 * "nothing open" from no snapshot at all.
 *
 * ## Which brokers count
 *
 * Only CONNECTED ones. A disconnected broker cannot be trading, and the engine
 * drops its cached positions on disconnect anyway, so waiting on one would
 * block the anchor forever.
 *
 * Every connected broker must have reported — not merely one of them. The sum
 * spans brokers because the fills do: `/api/trade-transactions` filters on
 * symbol alone, so a second broker's fills are already interleaved into the
 * walk. If that broker has not reported, its size is missing from the sum and
 * the anchor is quietly wrong by exactly that much, which is the failure mode
 * this whole mechanism exists to prevent. No connected broker at all is
 * likewise unknown rather than flat.
 *
 * Accounts are NOT filtered, deliberately. The fills are fetched without an
 * account filter, so scoping the anchor to the selected or enabled accounts
 * would describe a different series than the one being walked.
 *
 * @param normalize Symbol normalizer, injected rather than imported so this
 *   stays pure — the real one reads a store. Broker symbols are contract-coded
 *   (`MNQU6`) and chart symbols may carry an exchange (`MNQU6.CME`); both fold
 *   to the same base.
 */
export function resolveNetPositionAnchor(
  brokers: readonly BrokerPositionSnapshot[],
  chartSymbol: string,
  normalize: (symbol: string) => string
): number | null {
  const connected = brokers.filter((b) => b.status === 'connected');
  if (connected.length === 0) return null;
  if (connected.some((b) => b.positionsLoadedAt === undefined)) return null;

  const base = normalize(chartSymbol);
  return connected.reduce(
    (total, broker) =>
      total +
      broker.positions.reduce(
        // Rows without a symbol are skipped. `currentLivePosition` shows them
        // rather than hide a position from you; here an unrelated instrument's
        // size would silently corrupt every entry/exit label on the chart.
        (sum, pos) =>
          pos.symbol && normalize(pos.symbol) === base ? sum + (pos.netPos || 0) : sum,
        0
      ),
    0
  );
}

/**
 * Fills to markers, oldest first.
 *
 * Every fill is walked through the net position, because that walk is the only
 * thing that can tell an entry from an exit: a fill moving the position AWAY
 * from flat opened or added, one moving it TOWARD flat reduced or closed. No
 * flag on the fill says which it was, and guessing from side alone would call
 * every sell an exit — wrong the moment you go short.
 *
 * ## Why the walk runs backward from `currentNetPos`
 *
 * The walk used to start at zero and run forward, which assumes the stored fill
 * history is complete and begins with the account flat. It is neither.
 *
 * Measured against one real account (95 MNQ fills, one account, one symbol):
 * the OLDEST stored fill is a `Sell 1`, and the running net over the whole
 * history reaches -42 and +16 for a trader who works in 1-4 lots. The history
 * plainly starts mid-position and drops fills throughout — by design, since the
 * recorder now discards a fill whose direction it cannot resolve rather than
 * guessing at one. So `net` computed forward from zero is off by an unknown
 * constant, and every classification downstream inherits that error. The
 * user-visible result: the sell that CLOSED a long was read as opening a short,
 * and the long drew as a short entry.
 *
 * The broker, by contrast, reports the current net position as fact. Anchoring
 * there and running backward makes each fill's before/after state derivable
 * without knowing where the history began:
 *
 * ```
 * after(last fill)  = currentNetPos
 * before(fill)      = after(fill) - signed(fill)
 * after(prev fill)  = before(fill)
 * ```
 *
 * Error now accumulates toward the OLDEST fills instead of the newest, which is
 * the right way round: the markers a user is looking at are the recent ones.
 * When the history really is complete and really does start flat, the backward
 * walk reproduces the forward walk exactly — `before(first)` works out to zero
 * — so this is strictly more general, not different.
 *
 * @param currentNetPos The broker's net position for this symbol right now:
 *   positive long, negative short, `0` flat. Pass `null` when it is not known
 *   yet (positions have not loaded) — no marker is drawn at all in that case,
 *   because an anchor guessed at zero reproduces exactly the bug above, and a
 *   confidently mislabelled trade is worse than a bare chart.
 *
 * Duplicate fills are dropped. The transactions collection still holds a large
 * backlog of double-written rows from before the insert became an upsert, and
 * without this each one would draw two markers on the same bar.
 *
 * Lot matching — which entry each close belongs to, for the connector lines and
 * for the realised P&L on each close — runs afterwards as a separate forward
 * pass over the same rows. See {@link matchClosures}. It is fed
 * `before(first fill)`, which the backward walk has already derived by the time
 * it finishes, so the two passes cannot disagree about where the history
 * started.
 *
 * @param bars Ascending chart bars, for the max adverse excursion on each
 *   close — see {@link AdverseExcursion}. OPTIONAL, and the optionality is the
 *   design: this is the only input that does not come from the fills, and
 *   requiring it would drag a chart into every caller and every test. Omitted,
 *   or covering a shorter window than the trade, the excursion comes back
 *   `null` and nothing else about the markers changes.
 */
/** One bot execution, reduced to what linking a signal to a fill needs. */
export interface BotSignalRecord {
  botId: string;
  /** When the bot decided, i.e. the signal bar. */
  signalTimestamp: number;
  /** The signal's price — the bar close the setup was read off. */
  signalPrice: number;
  /** When the bot acted. Fills land shortly after this, never before. */
  timestamp: number;
}

/**
 * How long after a bot acted its fill may arrive and still be linked.
 *
 * Generous on purpose: a market entry fills in milliseconds, but a RESTING
 * limit entry can sit for the whole of its expiry — six minutes on the med-tier
 * bot — before filling. Too tight a window and exactly the pullback entries
 * this feature is most useful for would draw no line.
 *
 * Bounded all the same, so a fill hours later cannot adopt a stale execution.
 */
export const SIGNAL_LINK_WINDOW_MS = 15 * 60 * 1000;

/**
 * Build a lookup from bot executions to the signal behind a fill.
 *
 * Correlated rather than stored, because a broker fill carries no trace of what
 * caused it: the engine records `signalTimestamp` on its own execution rows,
 * and those are the only place the link exists.
 *
 * Matching is on bot AND direction of time: the newest execution by the same
 * bot that ran AT OR BEFORE the fill, within the window. Direction matters —
 * an execution after the fill is a different trade, and picking the nearest in
 * absolute time would let the NEXT signal claim this fill.
 *
 * Returns a resolver rather than a map so the policy has one home and can be
 * tested without building markers.
 */
export function signalLinkResolver(
  executions: readonly BotSignalRecord[]
): (fill: FillRow) => { timestamp: number; price: number } | undefined {
  // Oldest first, so the scan below can stop at the first candidate going back.
  const byBot = new Map<string, BotSignalRecord[]>();
  for (const e of executions) {
    if (!e.botId || !Number.isFinite(e.signalTimestamp) || !Number.isFinite(e.signalPrice)) continue;
    const list = byBot.get(e.botId);
    if (list) list.push(e);
    else byBot.set(e.botId, [e]);
  }
  for (const list of byBot.values()) list.sort((a, b) => a.timestamp - b.timestamp);

  return (fill: FillRow) => {
    if (!fill.botId) return undefined;
    const list = byBot.get(fill.botId);
    if (!list) return undefined;

    let best: BotSignalRecord | undefined;
    for (const e of list) {
      // Executions are ordered, so once one is past the fill every later one is
      // too.
      if (e.timestamp > fill.timestamp) break;
      if (fill.timestamp - e.timestamp <= SIGNAL_LINK_WINDOW_MS) best = e;
    }
    return best ? { timestamp: best.signalTimestamp, price: best.signalPrice } : undefined;
  };
}

export function buildTradeMarkers(
  fills: FillRow[],
  currentNetPos: number | null,
  bars: readonly Bar[] = NO_BARS,
  /**
   * Resolves the signal behind a bot fill. Optional — without it markers are
   * exactly what they were, which is what every existing caller and test wants.
   */
  resolveSignal?: (fill: FillRow) => { timestamp: number; price: number } | undefined
): TradeMarker[] {
  // No anchor, no markers. See the @param note.
  if (currentNetPos === null || !Number.isFinite(currentNetPos)) return [];

  const seen = new Set<string>();
  const rows: WalkableFill[] = fills
    .filter(isFilled)
    .filter((f) => {
      // Prefer the broker's fill id; fall back to the natural key so a row
      // missing one is still de-duplicated rather than drawn twice.
      const key = keyOf(f);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    // Unusable rows are dropped BEFORE the walk rather than skipped inside it.
    // The backward walk has to know the last usable fill to start from, so it
    // cannot discover them as it goes the way the forward one did.
    .map((fill) => {
      const side = sideOf(fill.side);
      if (!side || !fill.quantity || !fill.price) return null;
      return { fill, side, signed: side === 'buy' ? fill.quantity : -fill.quantity };
    })
    .filter((r): r is WalkableFill => r !== null)
    .sort((a, b) => a.fill.timestamp - b.fill.timestamp);

  const out: TradeMarker[] = [];
  // Newest fill first: its `after` state is the one the broker just told us.
  let after = currentNetPos;

  for (let i = rows.length - 1; i >= 0; i--) {
    const { fill, side, signed } = rows[i];
    const before = after - signed;

    // A fill that changes the SIGN of the position closed the old one and
    // opened a new one the other way, in a single action. It is an entry
    // whatever the magnitudes do. Neither end may be flat: opening from flat
    // and closing to flat are not flips, they are the ordinary cases below.
    //
    // This used to be `|after| > |before|` alone, which got an exact flip wrong
    // — long 1 then sell 2 lands on -1, the magnitude is unchanged, so a real
    // short entry was classified an exit. For the stretch when only entries
    // were drawn that bug made the entry vanish from the chart outright; now it
    // would draw a left-pointing exit where a short was opened, and lose the
    // lot the flip closed along with it.
    const flipped = before !== 0 && after !== 0 && Math.sign(after) !== Math.sign(before);
    // Otherwise: moving away from flat opens or adds, toward flat reduces or
    // closes.
    const isEntry = flipped || Math.abs(after) > Math.abs(before);

    out.push({
      id: keyOf(fill),
      timestamp: fill.timestamp,
      price: fill.price,
      // The fill's own side, both ways round. On an entry it is also the
      // direction of the exposure left behind — buying opens or adds to a long,
      // and on a flip the sell that takes you short is itself a sell — so the
      // arrow points the way the position now bets. On an exit it is the
      // opposite of the position being closed, which is what makes a round trip
      // read as one hue answered by the other. See {@link TradeMarker.side}.
      side,
      qty: fill.quantity,
      kind: isEntry ? 'entry' : 'exit',
      // Exits carry no attribution: the bot that opened a position is not
      // evidence about who closed it. See the module note.
      botName: isEntry ? (fill.botName ?? null) : null,
      // Entries only, and only bot ones — same rule as `botName` above. An exit
      // has no signal of its own, and a hand-taken fill had none to begin with.
      signal: isEntry && fill.botName ? resolveSignal?.(fill) : undefined,
      // Both filled in below, once the lots have been matched.
      closes: NO_CLOSES,
      realized: null,
    });

    // Step the walk on EVERY fill, entry or not.
    after = before;
  }

  // Collected newest-first by the backward walk; callers want oldest-first.
  out.reverse();

  // `after` has now walked past the oldest fill, so it holds `before(first)` —
  // the position the stored history begins already holding. Derived rather than
  // assumed zero, for the same reason the walk runs backward at all.
  const closures = matchClosures(
    rows.map((r) => ({
      id: keyOf(r.fill),
      signed: r.signed,
      price: r.fill.price,
      timestamp: r.fill.timestamp,
    })),
    after
  );
  // The symbol is the fill's own, not the chart's: the two disagree across a
  // contract roll, and it is the traded contract that carries the point value.
  const symbolOf = new Map(rows.map((r) => [keyOf(r.fill), r.fill.symbol]));
  for (const marker of out) {
    const closed = closures.get(marker.id);
    if (!closed) continue;
    const ids = entryIdsOf(closed);
    // Left as the shared empty array when a close named no entry — see
    // {@link pairClosures}. `realized` is still set in that case, because the
    // quantity was real even though the entry is unknowable.
    if (ids.length > 0) marker.closes = ids;
    marker.realized = realizedFromLots(
      closed,
      {
        price: marker.price,
        timestamp: marker.timestamp,
        side: marker.side,
        symbol: symbolOf.get(marker.id) ?? '',
      },
      bars
    );
  }

  return out;
}

/**
 * The bar that contains `fillTimeSec` — the last bar time at or before it.
 *
 * Why this exists: a chart can only place a marker on a time it actually has a
 * data point for. `timeScale.timeToCoordinate()` returns `null` for anything
 * else, and the caller has no choice but to drop the marker. Fills happen at
 * wall-clock times (17:50:15, 17:51:02, 17:59:30) while bars sit on the
 * configured bar interval, so a raw fill time almost never IS a data point —
 * measured against one real session's fills, only 6 of 16 landed on a bar
 * boundary, and in the window actually on screen exactly one did. The feature
 * looked broken. Snapping down to the containing bar is what makes a fill
 * placeable at all.
 *
 * The bar times come from the series' own data rather than from
 * `Math.floor(t / interval) * interval`: bars are not guaranteed to be
 * epoch-aligned, the interval is configurable (`BAR_INTERVAL`), and a second
 * copy of it here would drift out of step with the one the chart uses.
 *
 * `barTimes` must be ascending — series data is time-ordered, so it is. Both
 * arguments are in SECONDS, the unit lightweight-charts uses for bar times.
 *
 * Binary search, not a scan: this runs once per marker on every redraw, and
 * there can be hundreds of markers over thousands of bars.
 *
 * @returns the containing bar time, or `null` if there isn't one.
 */
export function snapToBar(fillTimeSec: number, barTimes: readonly number[]): number | null {
  // Nothing plotted yet — nowhere to put anything.
  if (barTimes.length === 0) return null;
  // Older than the oldest loaded bar. There is no bar containing it, and
  // guessing the nearest one would draw the trade at a time it did not happen.
  if (fillTimeSec < barTimes[0]) return null;

  // Rightmost bar with time <= fillTimeSec.
  //
  // A fill NEWER than the last bar falls out of this naturally, pinned to the
  // last bar, and that is deliberate: a trade you just took lives in the bar
  // still forming, which the chart may not have drawn yet. Dropping it would
  // mean your own fill takes up to a bar interval to appear.
  let lo = 0;
  let hi = barTimes.length - 1;
  while (lo < hi) {
    // Round the midpoint UP so `lo` always advances and the loop terminates.
    const mid = lo + Math.ceil((hi - lo) / 2);
    if (barTimes[mid] <= fillTimeSec) lo = mid;
    else hi = mid - 1;
  }
  return barTimes[lo];
}
